// RRAlertSplash.js - WITH NEWS FEED ON RIGHT SIDE
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const NEWS_API_URL = process.env.REACT_APP_NEWS_API || '/api/news82';
const SHOW_API     = '/api/show/api/show';

const EXCHANGE_FLAG = {
  SA:'br', T:'jp', KS:'kr', L:'gb', DE:'de', PA:'fr', AS:'nl', MI:'it',
  ST:'se', OL:'no', SW:'ch', BR:'be', VI:'at', HE:'fi', IR:'ie', AX:'au',
  NZ:'nz', SI:'sg', JK:'id', KL:'my', NS:'in', HK:'hk', TW:'tw', SS:'cn',
  SR:'sa', AE:'ae', KW:'kw', QA:'qa', TA:'il', TO:'ca', MX:'mx', SN:'cl',
};

function getFlagUrl(ticker) {
  if (!ticker) return null;
  if (!ticker.includes('.')) return 'https://flagcdn.com/w20/us.png';
  const suf = ticker.split('.').pop().toUpperCase();
  const cc = EXCHANGE_FLAG[suf];
  return cc ? `https://flagcdn.com/w20/${cc}.png` : null;
}

function fmtAlertPrice(alert) {
  if (!alert) return null;
  const price = alert.price_brl > 0 ? alert.price_brl : alert.price;
  if (!price || price <= 0) return 'N/A';
  const ccy = alert.currency;
  if (!ccy || ccy === 'BRL') return `R$ ${price.toFixed(2)}`;
  const noDecimals = ['JPY','KRW','IDR','CLP','TWD'].includes(ccy);
  return `${ccy} ${noDecimals ? Math.round(alert.price) : alert.price?.toFixed(2)}`;
}

const RRAlertSingleCard = ({ theme, marketData, regionFilter }) => {
  const [currentAlert, setCurrentAlert] = useState(null);
  const [alertQueue, setAlertQueue] = useState([]);
  const [alerted, setAlerted] = useState(new Set());
  const [isVisible, setIsVisible] = useState(false);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const processingRef = useRef(false);
  const navigate = useNavigate();

  // ============================================
  // FETCH FROM DINO :6060/api/show (worldwide)
  // ============================================
  useEffect(() => {
    let alive = true;
    const fetchShow = async () => {
      try {
        const res = await fetch(SHOW_API);
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        const incoming = [...(data.alerts || []), ...(data.volume_movers || [])];
        const newAlerts = [];
        const currentAlerted = new Set(alerted);
        incoming.forEach(a => {
          const region = getRegion(a.ticker);
          const inRegion = !regionFilter || regionFilter === region ||
            (regionFilter === 'europe_gulf' && (region === 'europe' || region === 'gulf'));
          if (Math.abs(a.change) >= 5 && !currentAlerted.has(a.ticker) && inRegion) {
            newAlerts.push({
              ticker:         a.ticker,
              name:           a.name,
              price:          a.price,
              price_brl:      a.price_brl,
              currency:       a.currency,
              change:         a.change,
              type:           a.direction === 'up' ? 'gain' : 'loss',
              isExtreme:      a.severity === 'extreme',
              prefetchedNews: a.news || [],
            });
            currentAlerted.add(a.ticker);
          }
        });
        if (newAlerts.length > 0) {
          setAlerted(currentAlerted);
          setAlertQueue(prev => [...prev, ...newAlerts]);
        }
      } catch (err) {
        console.error('RRAlertSplash: DINO fetch error', err);
      }
    };
    fetchShow();
    const id = setInterval(fetchShow, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ============================================
  // FETCH NEWS FOR CURRENT ALERT
  // ============================================
  useEffect(() => {
    if (!currentAlert) return;

    // Use pre-fetched news from :6060 payload if available
    if (currentAlert.prefetchedNews?.length) {
      setNews(currentAlert.prefetchedNews.slice(0, 3));
      setNewsLoading(false);
      return;
    }

    const fetchNews = async () => {
      setNewsLoading(true);
      try {
        const response = await fetch(`${NEWS_API_URL}/api/news/ticker/${currentAlert.ticker}`);
        
        if (response.status === 202) {
          // News being fetched, try again in 3 seconds
          setTimeout(fetchNews, 3000);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          setNews(data.news?.slice(0, 3) || []); // Top 3 news
          setNewsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching alert news:', err);
        setNewsLoading(false);
      }
    };
    
    fetchNews();
  }, [currentAlert]);

  // ============================================
  // PROCESS MARKET DATA & BUILD QUEUE
  // ============================================
  useEffect(() => {
    if (!marketData || !marketData.topGainers || !marketData.topLosers) return;

    const newAlerts = [];
    const currentAlerted = new Set(alerted);

    // Check Top Gainers
    marketData.topGainers.forEach(ticker => {
      const absChange = Math.abs(ticker.var);
      if (absChange >= 5 && !currentAlerted.has(ticker.ticker)) {
        newAlerts.push({
          ticker: ticker.ticker,
          name: ticker.name,
          price: ticker.last,
          change: ticker.var,
          type: 'gain',
          isExtreme: absChange >= 10
        });
        currentAlerted.add(ticker.ticker);
      }
    });

    // Check Top Losers
    marketData.topLosers.forEach(ticker => {
      const absChange = Math.abs(ticker.var);
      if (absChange >= 5 && !currentAlerted.has(ticker.ticker)) {
        newAlerts.push({
          ticker: ticker.ticker,
          name: ticker.name,
          price: ticker.last,
          change: ticker.var,
          type: 'loss',
          isExtreme: absChange >= 10
        });
        currentAlerted.add(ticker.ticker);
      }
    });

    if (newAlerts.length > 0) {
      setAlerted(currentAlerted);
      setAlertQueue(prev => [...prev, ...newAlerts]);
    }
  }, [marketData]);

  // ============================================
  // QUEUE PROCESSOR
  // ============================================
  useEffect(() => {
    if (alertQueue.length === 0 || processingRef.current || currentAlert) return;

    processingRef.current = true;
    
    const nextAlert = alertQueue[0];
    setCurrentAlert(nextAlert);
    setIsVisible(true);
    setNews([]); // Reset news

    setAlertQueue(prev => prev.slice(1));

    setTimeout(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setCurrentAlert(null);
        processingRef.current = false;
      }, 500);
    }, 8000); // Show for 8 seconds (more time to read news)

  }, [alertQueue, currentAlert]);

  // ============================================
  // NAVIGATION
  // ============================================
  const handleViewDetails = () => {
    if (!currentAlert) return;
    navigate(`/asset/stock/${currentAlert.ticker}`);
    setIsVisible(false);
    setTimeout(() => {
      setCurrentAlert(null);
      processingRef.current = false;
    }, 300);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentAlert(null);
      processingRef.current = false;
    }, 500);
  };

  // ============================================
  // RENDER
  // ============================================
  if (!currentAlert) return null;

  const isGain = currentAlert.type === 'gain';
  const bgGradient = isGain
    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.98))'
    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.98))';

  return (
    <div style={{ position: 'relative', width: '100%', pointerEvents: 'auto' }}>
      <div style={{
        background: bgGradient,
        borderRadius: '16px',
        padding: '20px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
      }}>
        
        {/* TWO COLUMN LAYOUT */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px'
        }}>
          
          {/* ========== LEFT: ALERT INFO ========== */}
          <div>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: '#ffffff',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {isGain ? '🚀' : '📉'} ALERTA
              </div>
              
              {currentAlert.isExtreme && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  padding: '3px 10px',
                  borderRadius: '8px',
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  textTransform: 'uppercase'
                }}>
                  ⚡ EXTREMO
                </div>
              )}
            </div>

            {/* Ticker Info */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '4px' }}>
                {getFlagUrl(currentAlert.ticker) && (
                  <img src={getFlagUrl(currentAlert.ticker)} alt=""
                    style={{ width: 20, height: 13, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                  {currentAlert.ticker}
                </div>
              </div>
              <div style={{
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '8px'
              }}>
                {currentAlert.name}
              </div>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: 'bold',
                color: '#ffffff',
                marginBottom: '4px'
              }}>
                {fmtAlertPrice(currentAlert)}
              </div>
              <div style={{
                fontSize: '1.6rem',
                fontWeight: 'bold',
                color: '#ffffff',
                textShadow: '0 2px 8px rgba(0,0,0,0.4)'
              }}>
                {isGain ? '▲' : '▼'} {Math.abs(currentAlert.change).toFixed(2)}%
              </div>
            </div>

            {/* Queue Indicator */}
            {alertQueue.length > 0 && (
              <div style={{
                padding: '6px',
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '6px',
                fontSize: '0.7rem',
                color: 'rgba(255, 255, 255, 0.9)',
                textAlign: 'center',
                marginBottom: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                +{alertQueue.length} na fila
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px'
            }}>
              <button
                onClick={handleViewDetails}
                style={{
                  padding: '10px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '8px',
                  color: isGain ? '#059669' : '#dc2626',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  textTransform: 'uppercase',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}
                onMouseOver={e => {
                  e.target.style.background = 'rgba(255, 255, 255, 1)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={e => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                📊 Detalhes
              </button>

              <button
                onClick={handleDismiss}
                style={{
                  padding: '10px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  textTransform: 'uppercase'
                }}
                onMouseOver={e => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.35)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={e => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {alertQueue.length > 0 ? '⏭️ Próximo' : '✕ Fechar'}
              </button>
            </div>
          </div>

          {/* ========== RIGHT: NEWS FEED ========== */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            padding: '12px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            maxHeight: '320px',
            overflowY: 'auto'
          }}>
            <div style={{
              fontSize: '0.8rem',
              fontWeight: 'bold',
              color: '#ffffff',
              marginBottom: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              📰 Notícias Recentes
            </div>

            {/* Loading State */}
            {newsLoading && news.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem'
              }}>
                Carregando notícias...
              </div>
            )}

            {/* No News State */}
            {!newsLoading && news.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.75rem'
              }}>
                Nenhuma notícia recente
              </div>
            )}

            {/* News List */}
            {news.map((item, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '10px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onClick={() => window.open(item.url, '_blank')}
              >
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  marginBottom: '4px',
                  lineHeight: '1.3',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {item.title}
                </div>
                <div style={{
                  fontSize: '0.65rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>{item.source}</span>
                  {item.sentiment && (
                    <>
                      <span>•</span>
                      <span>{getSentimentEmoji(item.sentiment)}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function
const getSentimentEmoji = (sentiment) => {
  switch(sentiment) {
    case 'positive': return '📈';
    case 'negative': return '📉';
    default: return '📰';
  }
};



// ── Region helper ────────────────────────────────────────────
function getRegion(ticker) {
  if (!ticker) return 'b3';
  if (/^[A-Z0-9]{4}3[2-5]$/.test(ticker)) return 'b3';
  if (!ticker.includes('.')) return 'americas';
  const suf = ticker.split('.').pop().toUpperCase();
  if (suf === 'SA') return 'b3';
  if (['TO','MX','SN'].includes(suf)) return 'americas';
  if (['SR','AE','KW','QA','TA'].includes(suf)) return 'gulf';
  if (['L','DE','PA','AS','ST','OL','SW','BR','VI','HE','IR','MI','AT'].includes(suf)) return 'europe';
  if (['T','KS','TW','AX','NZ','SI','JK','KL','NS','HK','SS','SZ'].includes(suf)) return 'asia';
  return 'other';
}

const SEGMENTS = [
  { key: 'b3',          label: '🇧🇷 B3'            },
  { key: 'americas',    label: '🌎 Américas'        },
  { key: 'asia',        label: '🌏 Ásia/Pacífico'   },
  { key: 'europe_gulf', label: '🌍 Europa & Golfo'  },
];

// ── 4-card grid export ───────────────────────────────────────
export const RRAlertCard = ({ theme, marketData }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [extremeCount, setExtremeCount] = React.useState(0);
  const [totalCount, setTotalCount]     = React.useState(0);

  // count extremes for header badge
  React.useEffect(() => {
    fetch(SHOW_API).then(r => r.json()).then(d => {
      const all = [...(d.alerts || []), ...(d.volume_movers || [])];
      setTotalCount(all.length);
      setExtremeCount(all.filter(a => a.severity === 'extreme').length);
    }).catch(() => {});
  }, []);

  return (
    <div style={{ width: '100%' }}>
      {/* ── Hot header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1rem', flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 3, height: 28, background: 'linear-gradient(180deg,#f43f5e,rgba(244,63,94,0))', borderRadius: 2, flexShrink: 0 }} />
          <span style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: '1.6rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>
            Alertas Globais
          </span>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, color: '#6660fe', border: '1px solid #6660fe55', padding: '2px 8px', borderRadius: 3, letterSpacing: '0.14em' }}>
            DINO
          </span>
          {extremeCount > 0 && (
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 800, color: '#f43f5e', border: '1px solid #f43f5e55', padding: '2px 8px', borderRadius: 3, letterSpacing: '0.1em', animation: 'pulse 1.5s infinite' }}>
              ⚡ {extremeCount} EXTREMOS
            </span>
          )}
          {totalCount > 0 && (
            <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
              {totalCount} ativos
            </span>
          )}
        </div>
        <a href="/alertas" style={{
          fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800,
          color: '#fff', letterSpacing: '0.1em', textDecoration: 'none',
          background: 'linear-gradient(90deg,#3730a3,#6660fe)',
          padding: '7px 16px', borderRadius: 5,
          border: '1px solid #818cf8',
          boxShadow: '0 0 14px #6660fe44',
          transition: 'box-shadow 0.2s',
        }}
          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 22px #6660fe88'}
          onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 14px #6660fe44'}
        >
          VER PAINEL COMPLETO →
        </a>
      </div>

      {/* ── 2×2 grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.25rem' }}>
        {SEGMENTS.map(seg => (
          <div key={seg.key} style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700,
              color: 'rgba(255,255,255,0.35)', letterSpacing: '0.15em', marginBottom: 6 }}>
              {seg.label.toUpperCase()}
            </div>
            <RRAlertSingleCard theme={theme} marketData={marketData} regionFilter={seg.key} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RRAlertCard;